const MEBIBYTE = 1024 * 1024;

export const LANDING_IMAGE_MAX_BYTES = 8 * MEBIBYTE;
export const PRODUCT_IMAGE_MAX_SOURCE_BYTES = 25 * MEBIBYTE;

const FORMAT_LABELS = new Map([
  ["image/jpeg", "JPG"],
  [".jpg", "JPG"],
  [".jpeg", "JPG"],
  ["image/png", "PNG"],
  [".png", "PNG"],
  ["image/webp", "WebP"],
  [".webp", "WebP"],
  ["image/svg+xml", "SVG"],
  [".svg", "SVG"],
  ["image/avif", "AVIF"],
  [".avif", "AVIF"],
  ["image/gif", "GIF"],
  [".gif", "GIF"],
  ["image/x-icon", "ICO"],
  ["image/vnd.microsoft.icon", "ICO"],
  [".ico", "ICO"],
]);

function normalizeAccept(accept = "") {
  return String(accept || "")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function extensionOf(filename = "") {
  const match = String(filename || "").toLowerCase().match(/(\.[a-z0-9]+)$/);
  return match?.[1] || "";
}

function clampProgress(value) {
  if (!Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function formatImageSize(bytes = 0) {
  const value = Math.max(0, Number(bytes || 0));
  if (value < 1024) return `${Math.max(1, Math.round(value))} B`;
  if (value < MEBIBYTE) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / MEBIBYTE).toFixed(value >= 10 * MEBIBYTE ? 0 : 1)} MB`;
}

export function acceptedImageFormatsLabel(accept = "") {
  const labels = [];
  normalizeAccept(accept).forEach((token) => {
    const label = FORMAT_LABELS.get(token);
    if (label && !labels.includes(label)) labels.push(label);
  });
  return labels.join(", ") || "formato de imagen compatible";
}

export function validateImageFile(
  file,
  {
    accept = "image/*",
    maxBytes = LANDING_IMAGE_MAX_BYTES,
    fieldLabel = "imagen",
  } = {},
) {
  if (!file || typeof file.name !== "string") {
    throw new Error(`Seleccioná un archivo válido para ${fieldLabel}.`);
  }

  const size = Number(file.size || 0);
  if (!Number.isFinite(size) || size <= 0) {
    throw new Error(`"${file.name}" está vacío o no se pudo leer.`);
  }

  const acceptTokens = normalizeAccept(accept);
  const mime = String(file.type || "").trim().toLowerCase();
  const extension = extensionOf(file.name);
  const acceptsAnyImage = acceptTokens.includes("image/*");
  const mimeAllowed = Boolean(
    mime &&
      (acceptsAnyImage ||
        acceptTokens.includes(mime) ||
        (mime === "image/vnd.microsoft.icon" && acceptTokens.includes("image/x-icon"))),
  );
  const extensionAllowed = Boolean(extension && acceptTokens.includes(extension));

  if (acceptTokens.length && !mimeAllowed && !extensionAllowed) {
    throw new Error(
      `"${file.name}" no tiene un formato permitido. Usá ${acceptedImageFormatsLabel(accept)}.`,
    );
  }

  const limit = Number(maxBytes || 0);
  if (Number.isFinite(limit) && limit > 0 && size > limit) {
    throw new Error(
      `"${file.name}" pesa ${formatImageSize(size)} y el máximo es ${formatImageSize(limit)}. Reducila o elegí otra imagen.`,
    );
  }

  return file;
}

export function uploadProgressPercent(progressEvent) {
  const directProgress = Number(progressEvent?.progress);
  if (Number.isFinite(directProgress)) {
    return clampProgress(directProgress * 100);
  }

  const loaded = Number(progressEvent?.loaded);
  const total = Number(progressEvent?.total);
  if (Number.isFinite(loaded) && Number.isFinite(total) && total > 0) {
    return clampProgress((loaded / total) * 100);
  }

  return null;
}

export function describeImageUploadError(
  error,
  {
    action = "subir la imagen",
    maxBytes = LANDING_IMAGE_MAX_BYTES,
  } = {},
) {
  const status = Number(error?.status || error?.response?.status || 0);
  const message = String(error?.message || "").trim();
  const normalized = message.toLowerCase();

  if (status === 413 || normalized.includes("limit_file_size")) {
    if (message && !normalized.includes("request failed")) return message;
    return `La imagen supera el máximo de ${formatImageSize(maxBytes)}. Reducila y volvé a intentar.`;
  }
  if (
    normalized.includes("timeout") ||
    normalized.includes("tiempo de espera") ||
    normalized.includes("aborted")
  ) {
    return `La carga tardó demasiado. Revisá tu conexión y volvé a intentar ${action}.`;
  }
  if (
    normalized.includes("network error") ||
    normalized.includes("error de conexión") ||
    normalized.includes("failed to fetch")
  ) {
    return `No se pudo conectar con el servidor para ${action}. Revisá tu conexión e intentá nuevamente.`;
  }
  if (message) return message;
  return `No se pudo ${action}. Volvé a intentar.`;
}
